import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import {
  deleteDocumentoImpianto,
  getDocumentoDownloadUrl,
  listDocumentiImpianto,
  type DocumentoImpianto,
  type DocumentoImpiantoTipo,
} from '../api/documentiImpianto';
import UploadDocumentoImpiantoModal from '../components/modals/UploadDocumentoImpiantoModal';
import EditDocumentoImpiantoModal from '../components/modals/EditDocumentoImpiantoModal';
import { useAuthStore } from '../store/authStore';
import { canWriteDocumentiImpianto } from '../lib/constants';
import { openPdfInTab } from '../utils/pdfPreview';

const TIPO_BADGE: Record<DocumentoImpiantoTipo, string> = {
  normativa: 'bg-blue-100 text-blue-700',
  scheda_tecnica: 'bg-emerald-100 text-emerald-700',
  autorizzazione: 'bg-amber-100 text-amber-700',
  altro: 'bg-stone-100 text-ink-soft',
};

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function truncate(s: string | null, max = 50): string {
  if (!s) return '';
  return s.length <= max ? s : s.slice(0, max) + '…';
}

export default function DocumentiImpiantoPage() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  // Eccezione vs Settings: AMM INCLUSO nella scrittura documenti impianto.
  const canWrite = canWriteDocumentiImpianto(user?.role);

  const [docs, setDocs] = useState<DocumentoImpianto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtroTipo, setFiltroTipo] = useState<DocumentoImpiantoTipo | ''>('');
  const [showUpload, setShowUpload] = useState(false);
  const [editing, setEditing] = useState<DocumentoImpianto | null>(null);

  const load = useCallback(() => {
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await listDocumentiImpianto(filtroTipo || undefined);
        setDocs(data);
      } catch {
        setError(t('documenti.errore_caricamento'));
      } finally {
        setLoading(false);
      }
    })();
  }, [filtroTipo, t]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleDownload(doc: DocumentoImpianto) {
    const url = getDocumentoDownloadUrl(doc.id);
    if (doc.mime_type === 'application/pdf') {
      try {
        await openPdfInTab(url);
      } catch {
        alert(t('documenti.errore_apri'));
      }
      return;
    }
    const token = useAuthStore.getState().token;
    try {
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`status ${resp.status}`);
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = doc.nome;
      link.click();
      // NESSUN clearTimeout, e non e' una dimenticanza: questo timer DEVE
      // sopravvivere allo smontaggio. Revoca un object URL che il browser
      // sta ancora leggendo per il download; annullarlo all'unmount (uscire
      // dalla pagina subito dopo il click) romperebbe il download. Non tocca
      // stato React, quindi non e' la classe di timer che fa uscire vitest
      // con 1 — non applicargli lo stesso rimedio dei toast.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
    } catch {
      alert(t('documenti.errore_scarica'));
    }
  }

  async function handleDelete(doc: DocumentoImpianto) {
    if (!confirm(t('documenti.conferma_elimina', { nome: doc.nome }))) return;
    try {
      await deleteDocumentoImpianto(doc.id);
      load();
    } catch {
      alert(t('documenti.errore_elimina'));
    }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-ink-dark">{t('documenti.titolo')}</h1>
          <p className="text-sm text-ink-soft mt-0.5">{t('documenti.conteggio', { n: docs.length })}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="p-2 text-ink-soft hover:text-ink-dark border border-stone-border rounded-lg bg-white"
            title={t('documenti.aggiorna')}
          >
            <RefreshCw size={15} />
          </button>
          {canWrite && (
            <button
              type="button"
              onClick={() => setShowUpload(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors"
              data-testid="documenti-impianto-upload-button"
            >
              <Plus size={15} /> {t('documenti.carica')}
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <label className="text-xs text-ink-soft">{t('documenti.filtra_tipo')}</label>
        <select
          value={filtroTipo}
          onChange={(e) => setFiltroTipo(e.target.value as DocumentoImpiantoTipo | '')}
          className="text-sm border border-stone-border rounded px-2 py-1.5 bg-white focus:outline-none focus:border-sage"
          data-testid="documenti-impianto-filtro-tipo"
        >
          <option value="">{t('documenti.tutti')}</option>
          <option value="normativa">{t('documenti.tipo.normativa')}</option>
          <option value="scheda_tecnica">{t('documenti.tipo.scheda_tecnica')}</option>
          <option value="autorizzazione">{t('documenti.tipo.autorizzazione')}</option>
          <option value="altro">{t('documenti.tipo.altro')}</option>
        </select>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-accent-danger-bg border border-[#FECACA] rounded-lg text-sm text-accent-danger">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-ink-soft text-sm">
          {t('documenti.caricamento')}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-stone-mid">
          <table className="w-full text-sm" data-testid="documenti-impianto-list-table">
            <thead className="bg-stone-bg border-b border-stone-mid">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
                  {t('documenti.col_nome')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
                  {t('documenti.col_descrizione')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
                  {t('documenti.col_tipo')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
                  {t('documenti.col_dimensione')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
                  {t('documenti.col_caricato_il')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
                  {t('documenti.col_caricato_da')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-ink-soft uppercase tracking-wide">
                  {t('documenti.col_azioni')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-mid">
              {docs.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-ink-faint">
                    {t('documenti.vuoto')}
                  </td>
                </tr>
              )}
              {docs.map((d) => (
                <tr
                  key={d.id}
                  className="hover:bg-stone-50"
                  data-testid={`documenti-impianto-row-${d.id}`}
                >
                  <td className="px-4 py-3 text-ink-dark font-medium">{d.nome}</td>
                  <td className="px-4 py-3 text-ink-mid">
                    <span title={d.descrizione ?? ''}>{truncate(d.descrizione)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${TIPO_BADGE[d.tipo]}`}
                    >
                      {t(`documenti.tipo.${d.tipo}`)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-ink-mid text-xs whitespace-nowrap">
                    {formatSize(d.size_bytes)}
                  </td>
                  <td className="px-4 py-3 text-ink-mid text-xs whitespace-nowrap">
                    {formatDate(d.uploaded_at)}
                  </td>
                  <td className="px-4 py-3 text-ink-mid text-xs">{d.uploaded_by_name}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => handleDownload(d)}
                        className="p-1.5 text-ink-soft hover:text-sage rounded transition-colors"
                        title={d.mime_type === 'application/pdf' ? t('documenti.anteprima') : t('documenti.scarica')}
                        data-testid={`documenti-impianto-action-download-${d.id}`}
                      >
                        {d.mime_type === 'application/pdf' ? <Download size={14} /> : <Download size={14} />}
                      </button>
                      {canWrite && (
                        <>
                          <button
                            type="button"
                            onClick={() => setEditing(d)}
                            className="p-1.5 text-ink-soft hover:text-sage rounded transition-colors"
                            title={t('documenti.modifica')}
                            data-testid={`documenti-impianto-action-edit-${d.id}`}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(d)}
                            className="p-1.5 text-ink-soft hover:text-accent-danger rounded transition-colors"
                            title={t('documenti.elimina')}
                            data-testid={`documenti-impianto-action-delete-${d.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showUpload && (
        <UploadDocumentoImpiantoModal
          onClose={() => setShowUpload(false)}
          onSaved={() => {
            setShowUpload(false);
            load();
          }}
        />
      )}

      {editing && (
        <EditDocumentoImpiantoModal
          documento={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}
